import { RecordModel } from "../core";
import { components } from "../schema";
import RecordSelect from "../../components/fields/RecordSelect";
import { CurrencyModel } from "../coreModels";
import { UnitModel } from "./housing";
import { Form, Select } from "antd";
import {
  DeliveryPaymentField, DiscountField, DownPaymentField, HiddenUnitField, InstallmentCountField,
  InstallmentField, PaymentTypeSlot, PlanNameField, PlanOrderField, PlanTotalCheck,
} from "../../components/business/paymentPlans/PaymentPlanFields";
import { formatInstallments, formatPart } from "../../components/business/paymentPlans/planTerms";

export type IOpex = components['schemas']['Opex'];
export type ICapex = components['schemas']['Capex'];
export type IUnitCapex = components['schemas']['UnitCapex'];
export type IUnitOpex = {
  id: number;
  unit: number;
  opex: number;
  /** Format: int64 */
  amount: string;
  /** Format: uuid */
  currency: string;
  periodicity: string;
};

/** How to read one payment's amount. `remainder` is only for the delivery
 * payment: whatever the others leave, with no amount of its own. */
export type PaymentType = 'fixed' | 'percentage' | 'remainder';

/** `economics.UnitPaymentPlan`. Decimals arrive as strings; a null payment
 * means the plan has no such payment, which a brochure leaves out. Each
 * payment has its own type, because one plan can mix them. */
export type IUnitPaymentPlan = {
  id: number;
  unit: number;
  name: string;
  discount: string;
  discount_type: Exclude<PaymentType, 'remainder'>;
  down_payment: string | null;
  down_payment_type: Exclude<PaymentType, 'remainder'>;
  monthly_installment: string | null;
  installment_count: number | null;
  installment_type: Exclude<PaymentType, 'remainder'>;
  delivery_payment: string | null;
  delivery_payment_type: PaymentType;
  sort_index: number;
};

export const OpexModel: RecordModel<IOpex> = {
  name: "Operating Exp",
  path: "op-exps",
  selectQuery: "&fields=id,concept",
  tableQuery: "",
  toString: (r: IOpex): string => r.concept ? r.concept : r.toString(),
  customCreateFields: [
    {
      name: 'periodicity',
      component: (
        <Form.Item name="periodicty" label="Periodicity">
          <Select>
            <Select.Option value="weekly">Weekly</Select.Option>
            <Select.Option value="monthly">Monthly</Select.Option>
            <Select.Option value="yearly">Yearly</Select.Option>
          </Select>
        </Form.Item>
      )
    },
  ]
}

export const CapexModel: RecordModel<ICapex> = {
  name: "Capital Exp",
  path: "cap-exps",
  selectQuery: "&fields=id,concept",
  tableQuery: "",
  toString: (r: ICapex): string => r.concept ? r.concept : r.toString(),
  customTableMappings: {
    currency: { path : ['currency', 'iso']},
    item: { path : ['item', 'description']},
  },
  customCreateFields: [
    { name: 'currency', component: <RecordSelect recordModel={CurrencyModel} /> },
  ]
}

const MXN_CURRENCY_ID = "f36b4827-23a1-4a38-83c0-6640dd0c9f53";

export const UnitCapexModel: RecordModel<IUnitCapex> = {
  name: "Capital Expenditures",
  path: "unit-cap-exps",
  selectQuery: "",
  tableQuery: "&expand=unit,capex,currency",
  toString: (r: any): string => r.capex.concept ? r.capex.concept : r.toString(),
  customTableMappings: {
    currency: { path : ['currency', 'iso']},
    unit: { path : ['unit', 'name']},
    capex: { path : ['capex', 'concept']},
  },
  customCreateFields: [
    { name: 'unit', component: <RecordSelect recordModel={UnitModel} allowManage /> },
    { name: 'currency', component: <RecordSelect defaultValue={MXN_CURRENCY_ID} recordModel={CurrencyModel} allowManage /> },
    { name: 'capex', component: <RecordSelect formName="capex" recordModel={CapexModel} allowManage /> },
  ]
}

export const UnitOpexModel: RecordModel<IUnitOpex> = {
  name: "Operational Expenses",
  path: "unit-op-exps",
  selectQuery: "",
  tableQuery: "&expand=unit,opex,currency",
  toString: (r: any): string => r.opex.concept ? r.opex.concept : r.toString(),
  customTableMappings: {
    currency: { path : ['currency', 'iso']},
    unit: { path : ['unit', 'name']},
    opex: { path : ['opex', 'concept']},
  },
  customCreateFields: [
    { name: 'unit', component: <RecordSelect recordModel={UnitModel} allowManage /> },
    { name: 'currency', component: <RecordSelect defaultValue={MXN_CURRENCY_ID} recordModel={CurrencyModel} allowManage /> },
    { name: 'opex', component: <RecordSelect formName="opex" recordModel={OpexModel} allowManage /> },
  ]
}

export const UnitPaymentPlanModel: RecordModel<IUnitPaymentPlan> = {
  name: "Payment Plan",
  namePlural: "Payment Plans",
  path: "unit-payment-plans",
  selectQuery: "",
  tableQuery: "",
  toString: (r: IUnitPaymentPlan): string => r.name,
  defaultValues: {
    discount: 0, discount_type: 'percentage',
    down_payment_type: 'fixed', installment_type: 'fixed', delivery_payment_type: 'fixed',
  },
  customTableMappings: {
    id: { omit: true },
    sort_index: { omit: true },
    name: { sort_index: 0, width: 60 },
    discount: {
      title: 'Discount', align: 'right',
      render: r => Number(r.discount) ? formatPart(r.discount, r.discount_type) : '—',
      width: 60
    },
    down_payment: {
      title: 'Down payment', sort_index: 2, align: 'right',
      render: r => formatPart(r.down_payment, r.down_payment_type),
      width: 75
    },
    monthly_installment: {
      title: 'Installments', sort_index: 3, align: 'right',
      render: r => formatInstallments(r),
    },
    delivery_payment: {
      title: 'On delivery', sort_index: 4, align: 'right',
      render: r => formatPart(r.delivery_payment, r.delivery_payment_type),
    },
    //sort_index: { title: 'Order', sort_index: 5, width: 50 },
    installment_count: { omit: true },
    down_payment_type: { omit: true },
    installment_type: { omit: true },
    delivery_payment_type: { omit: true },
    discount_type: { omit: true },
  },
  customCreateFields: [
    { name: 'unit', component: HiddenUnitField },
    { name: 'name', component: PlanNameField },
    { name: 'discount', component: DiscountField },
    { name: 'discount_type', component: PaymentTypeSlot },
    { name: 'down_payment', component: DownPaymentField },
    { name: 'down_payment_type', component: PaymentTypeSlot },
    { name: 'monthly_installment', component: InstallmentField },
    { name: 'installment_count', component: InstallmentCountField },
    { name: 'installment_type', component: PaymentTypeSlot },
    { name: 'delivery_payment', component: DeliveryPaymentField },
    { name: 'delivery_payment_type', component: PaymentTypeSlot },
    { name: 'sort_index', component: PlanOrderField },
  ],
  createEndChildren: <PlanTotalCheck />,
}
